import { useState, useEffect } from 'react';
import Layout from '@/components/layout/Layout';
import { useAdminAuth, type OperadorRole } from '@/contexts/AdminAuthContext';
import {
  MODULOS_PERMISSOES,
  getNiveisAcesso,
  salvarNiveisAcesso,
  generateNivelId,
  getTodosIdsModulos,
  type NivelAcessoConfig,
  type ModuloPermissao,
} from '@/mocks/admin/niveisAcesso';

type TabType = 'usuarios' | 'niveis';

const CORES_DISPONIVEIS = [
  { value: 'bg-orange-100 text-orange-700', label: 'Laranja' },
  { value: 'bg-emerald-100 text-emerald-700', label: 'Verde' },
  { value: 'bg-sky-100 text-sky-700', label: 'Azul' },
  { value: 'bg-violet-100 text-violet-700', label: 'Violeta' },
  { value: 'bg-rose-100 text-rose-700', label: 'Rosa' },
  { value: 'bg-amber-100 text-amber-700', label: 'Âmbar' },
  { value: 'bg-cyan-100 text-cyan-700', label: 'Ciano' },
  { value: 'bg-gray-100 text-gray-600', label: 'Cinza' },
];

export default function GestaoAcessoPage() {
  const {
    session,
    operadoresDaEmpresa,
    empresaNome,
    empresaId,
    isAuthenticated,
    adicionarOperador,
    removerOperador,
    alterarRoleOperador,
    alternarStatusOperador,
    editarDadosOperador,
  } = useAdminAuth();

  const [activeTab, setActiveTab] = useState<TabType>('usuarios');
  const [niveis, setNiveis] = useState<NivelAcessoConfig[]>([]);

  // Users tab state
  const [showAddModal, setShowAddModal] = useState(false);
  const [editandoOperador, setEditandoOperador] = useState<string | null>(null);
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' } | null>(null);
  const [novoNome, setNovoNome] = useState('');
  const [novoEmail, setNovoEmail] = useState('');
  const [novoSenha, setNovoSenha] = useState('');
  const [novoCpf, setNovoCpf] = useState('');
  const [novoRole, setNovoRole] = useState<OperadorRole>('operador');
  const [formError, setFormError] = useState('');

  // Edit user modal state
  const [showEditUserModal, setShowEditUserModal] = useState(false);
  const [editUserId, setEditUserId] = useState<string | null>(null);
  const [editUserNome, setEditUserNome] = useState('');
  const [editUserEmail, setEditUserEmail] = useState('');
  const [editUserCpf, setEditUserCpf] = useState('');
  const [editUserSenha, setEditUserSenha] = useState('');
  const [editUserError, setEditUserError] = useState('');

  // Levels tab state
  const [nivelExpandido, setNivelExpandido] = useState<string | null>(null);
  const [showLevelModal, setShowLevelModal] = useState(false);
  const [editandoNivel, setEditandoNivel] = useState<NivelAcessoConfig | null>(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState<NivelAcessoConfig | null>(null);
  const [formNivelNome, setFormNivelNome] = useState('');
  const [formNivelDescricao, setFormNivelDescricao] = useState('');
  const [formNivelCor, setFormNivelCor] = useState(CORES_DISPONIVEIS[0].value);
  const [formNivelPermissoes, setFormNivelPermissoes] = useState<string[]>([]);
  const [formNivelError, setFormNivelError] = useState('');

  useEffect(() => {
    if (empresaId) {
      setNiveis(getNiveisAcesso(empresaId));
    }
  }, [empresaId]);

  const isAdminEmpresa = session?.role === 'admin_empresa' || session?.isAdminEmpresa;
  const canManageLevels = isAdminEmpresa;

  const mostrarToast = (message: string, type: 'success' | 'error') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 3000);
  };

  const formatarCpf = (valor: string): string => {
    const apenasNumeros = valor.replace(/\D/g, '').slice(0, 11);
    if (apenasNumeros.length <= 3) return apenasNumeros;
    if (apenasNumeros.length <= 6) return `${apenasNumeros.slice(0, 3)}.${apenasNumeros.slice(3)}`;
    if (apenasNumeros.length <= 9) return `${apenasNumeros.slice(0, 3)}.${apenasNumeros.slice(3, 6)}.${apenasNumeros.slice(6)}`;
    return `${apenasNumeros.slice(0, 3)}.${apenasNumeros.slice(3, 6)}.${apenasNumeros.slice(6, 9)}-${apenasNumeros.slice(9, 11)}`;
  };

  const validarCpf = (cpf: string): boolean => {
    const apenasNumeros = cpf.replace(/\D/g, '');
    if (apenasNumeros.length !== 11) return false;
    if (/^(\d)\1{10}$/.test(apenasNumeros)) return false;
    let soma = 0;
    let resto = 0;
    for (let i = 1; i <= 9; i++) {
      soma += parseInt(apenasNumeros.charAt(i - 1)) * (11 - i);
    }
    resto = (soma * 10) % 11;
    if (resto === 10 || resto === 11) resto = 0;
    if (resto !== parseInt(apenasNumeros.charAt(9))) return false;
    soma = 0;
    for (let i = 1; i <= 10; i++) {
      soma += parseInt(apenasNumeros.charAt(i - 1)) * (12 - i);
    }
    resto = (soma * 10) % 11;
    if (resto === 10 || resto === 11) resto = 0;
    if (resto !== parseInt(apenasNumeros.charAt(10))) return false;
    return true;
  };

  const recarregarNiveis = () => {
    if (empresaId) {
      setNiveis(getNiveisAcesso(empresaId));
    }
  };

  // ─── Helpers for levels ────────────────────────────────
  const getNivelInfo = (roleKey: string): NivelAcessoConfig | undefined => {
    return niveis.find((n) => n.roleKey === roleKey);
  };

  const getRoleLabel = (roleKey: string): string => {
    return getNivelInfo(roleKey)?.nome || roleKey;
  };

  const getRoleColor = (roleKey: string): string => {
    return getNivelInfo(roleKey)?.cor || 'bg-gray-100 text-gray-600';
  };

  const getRoleDescricao = (roleKey: string): string => {
    return getNivelInfo(roleKey)?.descricao || 'Nível personalizado';
  };

  const roleKeys = niveis.map((n) => n.roleKey);

  // ─── Users handlers ─────────────────────────────────────
  const handleAdicionarOperador = () => {
    setFormError('');
    if (!novoNome.trim()) {
      setFormError('Nome é obrigatório');
      return;
    }
    if (!novoEmail.trim() || !novoEmail.includes('@')) {
      setFormError('Email válido é obrigatório');
      return;
    }
    if (!novoCpf.trim()) {
      setFormError('CPF é obrigatório');
      return;
    }
    if (!validarCpf(novoCpf)) {
      setFormError('CPF inválido');
      return;
    }
    if (!novoSenha.trim() || novoSenha.length < 4) {
      setFormError('Senha deve ter pelo menos 4 caracteres');
      return;
    }

    const result = adicionarOperador(novoNome.trim(), novoEmail.trim(), novoSenha.trim(), novoRole, novoCpf.trim());
    if (result.success) {
      mostrarToast(`${novoNome} adicionado como ${getRoleLabel(novoRole)}`, 'success');
      setShowAddModal(false);
      setNovoNome('');
      setNovoEmail('');
      setNovoSenha('');
      setNovoCpf('');
      setNovoRole('operador');
    } else {
      setFormError(result.error || 'Erro ao adicionar operador');
    }
  };

  const handleRemoverOperador = (operadorId: string, nome: string) => {
    removerOperador(operadorId);
    mostrarToast(`${nome} removido do acesso`, 'success');
  };

  const handleAlternarStatus = (operadorId: string, nome: string) => {
    const result = alternarStatusOperador(operadorId);
    if (result.success) {
      const msg = result.ativo ? `${nome} foi ativado` : `${nome} foi inativado`;
      mostrarToast(msg, 'success');
    } else {
      mostrarToast(result.error || 'Erro ao alterar status', 'error');
    }
  };

  const handleAlterarRole = (operadorId: string, novoRole: OperadorRole, nome: string) => {
    alterarRoleOperador(operadorId, novoRole);
    mostrarToast(`Papel de ${nome} alterado para ${getRoleLabel(novoRole)}`, 'success');
    setEditandoOperador(null);
  };

  const abrirEditarUsuario = (operador: typeof operadoresDaEmpresa[number]) => {
    setEditUserId(operador.id);
    setEditUserNome(operador.nome);
    setEditUserEmail(operador.email);
    setEditUserCpf(operador.cpf || '');
    setEditUserSenha(operador.senha || '');
    setEditUserError('');
    setShowEditUserModal(true);
  };

  const handleSalvarEdicaoUsuario = () => {
    setEditUserError('');
    if (!editUserId) return;
    if (!editUserNome.trim()) {
      setEditUserError('Nome é obrigatório');
      return;
    }
    if (!editUserEmail.trim() || !editUserEmail.includes('@')) {
      setEditUserError('Email válido é obrigatório');
      return;
    }
    if (!editUserCpf.trim()) {
      setEditUserError('CPF é obrigatório');
      return;
    }
    if (!validarCpf(editUserCpf)) {
      setEditUserError('CPF inválido');
      return;
    }
    if (!editUserSenha.trim() || editUserSenha.length < 4) {
      setEditUserError('Senha deve ter pelo menos 4 caracteres');
      return;
    }

    const result = editarDadosOperador(
      editUserId,
      editUserNome.trim(),
      editUserEmail.trim(),
      editUserCpf.trim(),
      editUserSenha.trim()
    );
    if (result.success) {
      mostrarToast(`${editUserNome.trim()} atualizado com sucesso`, 'success');
      setShowEditUserModal(false);
      setEditUserId(null);
      setEditUserNome('');
      setEditUserEmail('');
      setEditUserCpf('');
      setEditUserSenha('');
    } else {
      setEditUserError(result.error || 'Erro ao atualizar operador');
    }
  };

  // ─── Levels handlers ────────────────────────────────────
  const togglePermissao = (moduloId: string) => {
    setFormNivelPermissoes((prev) =>
      prev.includes(moduloId) ? prev.filter((id) => id !== moduloId) : [...prev, moduloId]
    );
  };

  const toggleModuloComSubmodulos = (modulo: ModuloPermissao) => {
    if (modulo.submodulos) {
      const subIds = modulo.submodulos.map((s) => s.id);
      const todosSelecionados = subIds.every((id) => formNivelPermissoes.includes(id));
      if (todosSelecionados) {
        setFormNivelPermissoes((prev) => prev.filter((id) => !subIds.includes(id)));
      } else {
        setFormNivelPermissoes((prev) => [...new Set([...prev, ...subIds])]);
      }
    } else {
      togglePermissao(modulo.id);
    }
  };

  const abrirCriarNivel = () => {
    setEditandoNivel(null);
    setFormNivelNome('');
    setFormNivelDescricao('');
    setFormNivelCor(CORES_DISPONIVEIS[0].value);
    setFormNivelPermissoes([]);
    setFormNivelError('');
    setShowLevelModal(true);
  };

  const abrirEditarNivel = (nivel: NivelAcessoConfig) => {
    setEditandoNivel(nivel);
    setFormNivelNome(nivel.nome);
    setFormNivelDescricao(nivel.descricao);
    setFormNivelCor(nivel.cor);
    setFormNivelPermissoes([...nivel.permissoes]);
    setFormNivelError('');
    setShowLevelModal(true);
  };

  const handleSalvarNivel = () => {
    setFormNivelError('');
    if (!formNivelNome.trim()) {
      setFormNivelError('Nome do nível é obrigatório');
      return;
    }
    if (!formNivelDescricao.trim()) {
      setFormNivelError('Descrição é obrigatória');
      return;
    }
    if (formNivelPermissoes.length === 0) {
      setFormNivelError('Selecione pelo menos uma permissão');
      return;
    }

    const updated = [...niveis];

    if (editandoNivel) {
      const idx = updated.findIndex((n) => n.id === editandoNivel.id);
      if (idx >= 0) {
        updated[idx] = {
          ...updated[idx],
          nome: formNivelNome.trim(),
          descricao: formNivelDescricao.trim(),
          cor: formNivelCor,
          permissoes: [...formNivelPermissoes],
        };
      }
    } else {
      const novoNivel: NivelAcessoConfig = {
        id: generateNivelId(),
        empresaId: empresaId || '',
        roleKey: `custom_${Date.now()}`,
        nome: formNivelNome.trim(),
        descricao: formNivelDescricao.trim(),
        cor: formNivelCor,
        permissoes: [...formNivelPermissoes],
        isDefault: false,
      };
      updated.push(novoNivel);
    }

    salvarNiveisAcesso(empresaId || '', updated);
    setNiveis(updated);
    setShowLevelModal(false);
    setEditandoNivel(null);
    mostrarToast(
      editandoNivel ? `Nível "${formNivelNome.trim()}" atualizado` : `Nível "${formNivelNome.trim()}" criado`,
      'success'
    );
  };

  const handleExcluirNivel = () => {
    if (!showDeleteConfirm) return;
    const updated = niveis.filter((n) => n.id !== showDeleteConfirm.id);
    salvarNiveisAcesso(empresaId || '', updated);
    setNiveis(updated);
    setShowDeleteConfirm(null);
    mostrarToast(`Nível "${showDeleteConfirm.nome}" removido`, 'success');
  };

  if (!isAuthenticated) return null;

  // ─── Tab definitions ────────────────────────────────────
  const tabs: { id: TabType; label: string; icon: string }[] = [
    { id: 'usuarios', label: 'Usuários', icon: 'ri-shield-user-line' },
    { id: 'niveis', label: 'Níveis de Acesso', icon: 'ri-settings-3-line' },
  ];

  return (
    <Layout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Gestão de Acesso</h1>
            <p className="text-sm text-gray-500 mt-1">
              Gerencie usuários e níveis de acesso do portal da {empresaNome || 'sua empresa'}
            </p>
          </div>
          <div className="flex items-center space-x-3">
            <div className="flex items-center space-x-2 text-sm text-gray-500 bg-gray-50 px-3 py-1.5 rounded-lg">
              <div className="w-4 h-4 flex items-center justify-center">
                <i className="ri-building-line text-gray-400"></i>
              </div>
              <span className="font-medium text-gray-700">{empresaNome}</span>
            </div>
            {activeTab === 'usuarios' && (
              <button
                onClick={() => setShowAddModal(true)}
                className="flex items-center space-x-2 px-4 py-2 bg-orange-500 hover:bg-orange-600 text-white font-bold text-sm rounded-lg transition-all cursor-pointer whitespace-nowrap shadow-md"
              >
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-user-add-line"></i>
                </div>
                <span>Adicionar Usuário</span>
              </button>
            )}
            {activeTab === 'niveis' && canManageLevels && (
              <button
                onClick={abrirCriarNivel}
                className="flex items-center space-x-2 px-4 py-2 bg-orange-500 hover:bg-orange-600 text-white font-bold text-sm rounded-lg transition-all cursor-pointer whitespace-nowrap shadow-md"
              >
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-add-line"></i>
                </div>
                <span>Criar Nível</span>
              </button>
            )}
          </div>
        </div>

        {/* Tab Switcher */}
        <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">
          <div className="flex border-b border-gray-100">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => {
                  setActiveTab(tab.id);
                  setNivelExpandido(null);
                }}
                className={`flex items-center space-x-2 px-6 py-3.5 text-sm font-bold transition-all cursor-pointer whitespace-nowrap border-b-2 ${
                  activeTab === tab.id
                    ? 'border-orange-500 text-orange-600 bg-orange-50/50'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:bg-gray-50'
                }`}
              >
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className={tab.icon}></i>
                </div>
                <span>{tab.label}</span>
              </button>
            ))}
          </div>

          {/* ─── TAB: USUÁRIOS ──────────────────────────── */}
          {activeTab === 'usuarios' && (
            <div className="p-0">
              {/* Info alert */}
              <div className="m-5 bg-amber-50 border border-amber-100 rounded-xl p-4 flex items-start space-x-3">
                <div className="w-5 h-5 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <i className="ri-information-line text-amber-500"></i>
                </div>
                <div>
                  <p className="text-sm font-medium text-amber-800">Permissões por nível</p>
                  <p className="text-xs text-amber-600 mt-1">
                    Os níveis de acesso são configurados na aba <strong>Níveis de Acesso</strong>.{' '}
                    {isAdminEmpresa && 'Você pode criar, editar e parametrizar os níveis por lá.'}
                  </p>
                  <div className="grid grid-cols-2 gap-2 mt-3">
                    {niveis.map((nivel) => (
                      <div key={nivel.id} className="flex items-start space-x-1.5">
                        <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-bold flex-shrink-0 mt-0.5 ${nivel.cor}`}>
                          {nivel.nome}
                        </span>
                        <span className="text-xs text-amber-700">{nivel.descricao}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* Lista de Operadores */}
              <div className="px-5 pb-5">
                <div className="bg-gray-50/50 rounded-lg px-4 py-2 mb-4 flex items-center justify-between">
                  <p className="text-xs font-bold text-gray-500 uppercase">
                    Usuários com acesso ({operadoresDaEmpresa.length})
                  </p>
                  <p className="text-xs text-gray-400">
                    Empresa: {empresaNome}
                  </p>
                </div>

                {operadoresDaEmpresa.length === 0 ? (
                  <div className="py-12 text-center">
                    <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                      <div className="w-8 h-8 flex items-center justify-center">
                        <i className="ri-user-line text-gray-400 text-2xl"></i>
                      </div>
                    </div>
                    <p className="text-sm text-gray-500">Nenhum operador cadastrado</p>
                    <button
                      onClick={() => setShowAddModal(true)}
                      className="mt-3 text-sm text-orange-500 hover:text-orange-600 font-bold cursor-pointer"
                    >
                      Adicionar primeiro usuário
                    </button>
                  </div>
                ) : (
                  <div className="divide-y divide-gray-50 border border-gray-100 rounded-lg overflow-hidden">
                    {operadoresDaEmpresa.map((operador) => {
                      const editando = editandoOperador === operador.id;
                      const isAdminEmpresaRole = operador.role === 'admin_empresa';
                      return (
                        <div key={operador.id} className="flex items-center justify-between px-4 py-3 hover:bg-gray-50/30 transition-colors">
                          <div className="flex items-center space-x-3">
                            <div className={`w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0 ${
                              operador.ativo ? 'bg-gradient-to-br from-orange-400 to-amber-500' : 'bg-gray-200'
                            }`}>
                              <span className="text-white font-bold text-xs">
                                {operador.nome.charAt(0).toUpperCase()}
                              </span>
                            </div>
                            <div>
                              <div className="flex items-center space-x-2">
                                <p className="text-sm font-bold text-gray-800">{operador.nome}</p>
                                {!operador.ativo && (
                                  <span className="text-[10px] bg-red-100 text-red-600 px-1.5 py-0.5 rounded-full font-bold">Inativo</span>
                                )}
                              </div>
                              <div className="flex items-center space-x-2">
                                <p className="text-xs text-gray-400">{operador.email}</p>
                                {operador.cpf && (
                                  <>
                                    <span className="text-gray-300">|</span>
                                    <p className="text-xs text-gray-400">{operador.cpf}</p>
                                  </>
                                )}
                              </div>
                            </div>
                          </div>

                          <div className="flex items-center space-x-2">
                            {editando ? (
                              <div className="flex items-center flex-wrap gap-1 bg-gray-50 rounded-lg px-2 py-1.5">
                                {roleKeys.map((rk) => (
                                  <button
                                    key={rk}
                                    onClick={() => handleAlterarRole(operador.id, rk as OperadorRole, operador.nome)}
                                    className={`px-2.5 py-1 rounded text-[10px] font-bold transition-all cursor-pointer whitespace-nowrap ${
                                      operador.role === rk
                                        ? 'bg-emerald-500 text-white shadow-sm'
                                        : 'text-gray-500 hover:bg-gray-200'
                                    }`}
                                  >
                                    {getRoleLabel(rk)}
                                  </button>
                                ))}
                                <button
                                  onClick={() => setEditandoOperador(null)}
                                  className="p-1 rounded hover:bg-gray-200 text-gray-400 cursor-pointer ml-1"
                                >
                                  <div className="w-3.5 h-3.5 flex items-center justify-center">
                                    <i className="ri-close-line text-xs"></i>
                                  </div>
                                </button>
                              </div>
                            ) : (
                              <>
                                <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-[10px] font-bold ${getRoleColor(operador.role)}`}>
                                  {getRoleLabel(operador.role)}
                                </span>

                                {!isAdminEmpresaRole && (
                                  <>
                                    <button
                                      onClick={() => abrirEditarUsuario(operador)}
                                      className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-400 hover:text-gray-600 cursor-pointer transition-colors"
                                      title="Editar dados"
                                    >
                                      <div className="w-4 h-4 flex items-center justify-center">
                                        <i className="ri-pencil-line text-sm"></i>
                                      </div>
                                    </button>
                                    <button
                                      onClick={() => setEditandoOperador(operador.id)}
                                      className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-400 hover:text-gray-600 cursor-pointer transition-colors"
                                      title="Alterar papel"
                                    >
                                      <div className="w-4 h-4 flex items-center justify-center">
                                        <i className="ri-edit-line text-sm"></i>
                                      </div>
                                    </button>
                                    <button
                                      onClick={() => handleAlternarStatus(operador.id, operador.nome)}
                                      className={`p-1.5 rounded-lg cursor-pointer transition-colors ${
                                        operador.ativo
                                          ? 'hover:bg-red-50 text-gray-400 hover:text-red-500'
                                          : 'hover:bg-emerald-50 text-gray-400 hover:text-emerald-500'
                                      }`}
                                      title={operador.ativo ? 'Inativar usuário' : 'Ativar usuário'}
                                    >
                                      <div className="w-4 h-4 flex items-center justify-center">
                                        <i className={`${operador.ativo ? 'ri-user-unfollow-line' : 'ri-user-follow-line'} text-sm`}></i>
                                      </div>
                                    </button>
                                    <button
                                      onClick={() => handleRemoverOperador(operador.id, operador.nome)}
                                      className="p-1.5 rounded-lg hover:bg-red-50 text-gray-400 hover:text-red-500 cursor-pointer transition-colors"
                                      title="Remover acesso"
                                    >
                                      <div className="w-4 h-4 flex items-center justify-center">
                                        <i className="ri-delete-bin-line text-sm"></i>
                                      </div>
                                    </button>
                                  </>
                                )}
                                {isAdminEmpresaRole && (
                                  <span className="text-[10px] text-gray-400 italic">Admin da empresa</span>
                                )}
                              </>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* ─── TAB: NÍVEIS DE ACESSO ───────────────────── */}
          {activeTab === 'niveis' && (
            <div className="p-0">
              {!canManageLevels && (
                <div className="m-5 bg-amber-50 border border-amber-100 rounded-xl p-4 flex items-start space-x-3">
                  <div className="w-5 h-5 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <i className="ri-information-line text-amber-500"></i>
                  </div>
                  <p className="text-sm text-amber-800">
                    Apenas o <strong>Administrador</strong> da empresa pode criar ou editar níveis de acesso.
                    Você está visualizando as configurações atuais.
                  </p>
                </div>
              )}

              <div className="px-5 pb-5">
                <div className="space-y-3">
                  {niveis.map((nivel) => {
                    const expandido = nivelExpandido === nivel.id;
                    return (
                      <div
                        key={nivel.id}
                        className="bg-white rounded-xl border border-gray-100 overflow-hidden transition-all"
                      >
                        <div
                          className="flex items-center justify-between px-5 py-4 cursor-pointer hover:bg-gray-50/50 transition-colors"
                          onClick={() => setNivelExpandido(expandido ? null : nivel.id)}
                        >
                          <div className="flex items-center space-x-4">
                            <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-bold ${nivel.cor}`}>
                              {nivel.nome}
                            </span>
                            <div>
                              <p className="text-sm text-gray-500">{nivel.descricao}</p>
                            </div>
                            {nivel.isDefault && (
                              <span className="text-[10px] bg-gray-100 text-gray-500 px-2 py-0.5 rounded-full font-medium">
                                Padrão
                              </span>
                            )}
                          </div>
                          <div className="flex items-center space-x-3">
                            <span className="text-xs text-gray-400">
                              {nivel.permissoes.length} {nivel.permissoes.length === 1 ? 'módulo' : 'módulos'}
                            </span>
                            <div className="w-5 h-5 flex items-center justify-center">
                              <i className={expandido ? 'ri-arrow-up-s-line text-gray-400' : 'ri-arrow-down-s-line text-gray-400'}></i>
                            </div>
                          </div>
                        </div>

                        {expandido && (
                          <div className="px-5 pb-5 border-t border-gray-50">
                            <div className="mt-4 space-y-3">
                              <p className="text-xs font-bold text-gray-500 uppercase tracking-wider">
                                Módulos com acesso
                              </p>
                              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
                                {MODULOS_PERMISSOES.map((modulo) => {
                                  if (modulo.submodulos) {
                                    const subsComAcesso = modulo.submodulos.filter((s) => nivel.permissoes.includes(s.id));
                                    const todos = modulo.submodulos.every((s) => nivel.permissoes.includes(s.id));
                                    return (
                                      <div key={modulo.id} className={`p-3 rounded-lg border ${subsComAcesso.length > 0 ? 'border-emerald-200 bg-emerald-50/50' : 'border-gray-100 bg-gray-50/30'}`}>
                                        <div className="flex items-center space-x-2 mb-1.5">
                                          <div className="w-4 h-4 flex items-center justify-center">
                                            <i className={`${modulo.icone} ${subsComAcesso.length > 0 ? 'text-emerald-600' : 'text-gray-400'} text-sm`}></i>
                                          </div>
                                          <span className={`text-xs font-bold ${subsComAcesso.length > 0 ? 'text-emerald-800' : 'text-gray-500'}`}>
                                            {modulo.nome}
                                          </span>
                                          {todos && subsComAcesso.length > 0 && (
                                            <span className="text-[9px] bg-emerald-200 text-emerald-700 px-1.5 py-0.5 rounded-full">Tudo</span>
                                          )}
                                          {!todos && subsComAcesso.length > 0 && (
                                            <span className="text-[9px] bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded-full">
                                              {subsComAcesso.length}/{modulo.submodulos.length}
                                            </span>
                                          )}
                                        </div>
                                        {modulo.submodulos.map((sub) => {
                                          const temAcesso = nivel.permissoes.includes(sub.id);
                                          return (
                                            <div key={sub.id} className="flex items-center space-x-1.5 ml-6 py-0.5">
                                              <div className="w-3 h-3 flex items-center justify-center">
                                                <i className={`ri-${temAcesso ? 'check-line text-emerald-500' : 'close-line text-gray-300'} text-xs`}></i>
                                              </div>
                                              <span className={`text-[11px] ${temAcesso ? 'text-gray-700 font-medium' : 'text-gray-400'}`}>
                                                {sub.nome}
                                              </span>
                                            </div>
                                          );
                                        })}
                                      </div>
                                    );
                                  }
                                  const temAcesso = nivel.permissoes.includes(modulo.id);
                                  return (
                                    <div key={modulo.id} className={`p-3 rounded-lg border flex items-center space-x-2 ${temAcesso ? 'border-emerald-200 bg-emerald-50/50' : 'border-gray-100 bg-gray-50/30'}`}>
                                      <div className="w-4 h-4 flex items-center justify-center">
                                        <i className={`${modulo.icone} ${temAcesso ? 'text-emerald-600' : 'text-gray-400'} text-sm`}></i>
                                      </div>
                                      <span className={`text-xs font-bold ${temAcesso ? 'text-emerald-800' : 'text-gray-500'}`}>
                                        {modulo.nome}
                                      </span>
                                    </div>
                                  );
                                })}
                              </div>
                            </div>

                            {canManageLevels && (
                              <div className="flex items-center space-x-2 mt-4 pt-3 border-t border-gray-50">
                                <button
                                  onClick={(e) => { e.stopPropagation(); abrirEditarNivel(nivel); }}
                                  className="flex items-center space-x-1.5 px-3 py-1.5 text-xs font-bold text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-lg cursor-pointer transition-colors whitespace-nowrap"
                                >
                                  <div className="w-3.5 h-3.5 flex items-center justify-center">
                                    <i className="ri-edit-line"></i>
                                  </div>
                                  <span>Editar</span>
                                </button>
                                {!nivel.isDefault && (
                                  <button
                                    onClick={(e) => { e.stopPropagation(); setShowDeleteConfirm(nivel); }}
                                    className="flex items-center space-x-1.5 px-3 py-1.5 text-xs font-bold text-red-600 bg-red-50 hover:bg-red-100 rounded-lg cursor-pointer transition-colors whitespace-nowrap"
                                  >
                                    <div className="w-3.5 h-3.5 flex items-center justify-center">
                                      <i className="ri-delete-bin-line"></i>
                                    </div>
                                    <span>Excluir</span>
                                  </button>
                                )}
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}

                  {niveis.length === 0 && (
                    <div className="py-12 text-center">
                      <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                        <div className="w-8 h-8 flex items-center justify-center">
                          <i className="ri-settings-3-line text-gray-400 text-2xl"></i>
                        </div>
                      </div>
                      <p className="text-sm text-gray-500">Nenhum nível configurado</p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* ─── MODAL: Adicionar Usuário ─────────────────── */}
        {showAddModal && (
          <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50" onClick={() => setShowAddModal(false)}>
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md mx-4 p-6" onClick={(e) => e.stopPropagation()}>
              <div className="flex items-center justify-between mb-5">
                <div>
                  <h3 className="text-lg font-bold text-gray-900">Adicionar Usuário</h3>
                  <p className="text-xs text-gray-500 mt-0.5">Conceda acesso ao portal da {empresaNome}</p>
                </div>
                <button onClick={() => setShowAddModal(false)} className="p-2 hover:bg-gray-100 rounded-lg cursor-pointer">
                  <div className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-close-line text-gray-400"></i>
                  </div>
                </button>
              </div>

              {formError && (
                <div className="mb-4 p-3 bg-red-50 border border-red-100 rounded-lg flex items-center space-x-2">
                  <div className="w-4 h-4 flex items-center justify-center flex-shrink-0">
                    <i className="ri-error-warning-line text-red-500 text-sm"></i>
                  </div>
                  <p className="text-xs text-red-600 font-medium">{formError}</p>
                </div>
              )}

              <div className="space-y-4">
                <div>
                  <label className="block text-xs font-bold text-gray-700 mb-1.5">Nome completo</label>
                  <input type="text" value={novoNome} onChange={(e) => setNovoNome(e.target.value)} placeholder="Nome do operador"
                    className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm text-gray-800 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-orange-500/30 focus:border-orange-500 transition-all" />
                </div>
                <div>
                  <label className="block text-xs font-bold text-gray-700 mb-1.5">Email</label>
                  <input type="email" value={novoEmail} onChange={(e) => setNovoEmail(e.target.value)} placeholder="operador@empresa.com.br"
                    className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm text-gray-800 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-orange-500/30 focus:border-orange-500 transition-all" />
                </div>
                <div>
                  <label className="block text-xs font-bold text-gray-700 mb-1.5">CPF</label>
                  <input type="text" value={novoCpf} onChange={(e) => setNovoCpf(formatarCpf(e.target.value))} placeholder="000.000.000-00"
                    className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm text-gray-800 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-orange-500/30 focus:border-orange-500 transition-all" />
                </div>
                <div>
                  <label className="block text-xs font-bold text-gray-700 mb-1.5">Senha de acesso</label>
                  <input type="text" value={novoSenha} onChange={(e) => setNovoSenha(e.target.value)} placeholder="Mínimo 4 caracteres"
                    className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm text-gray-800 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-orange-500/30 focus:border-orange-500 transition-all" />
                </div>
                <div>
                  <label className="block text-xs font-bold text-gray-700 mb-2">Nível de acesso</label>
                  <div className="grid grid-cols-2 gap-2">
                    {niveis.map((nivel) => (
                      <button key={nivel.id} onClick={() => setNovoRole(nivel.roleKey as OperadorRole)}
                        className={`p-2.5 rounded-lg border text-left transition-all cursor-pointer ${novoRole === nivel.roleKey ? 'border-orange-400 bg-orange-50 ring-1 ring-orange-300' : 'border-gray-200 hover:border-gray-300'}`}>
                        <span className={`text-[10px] font-bold ${nivel.cor} px-1.5 py-0.5 rounded-full`}>{nivel.nome}</span>
                        <p className="text-[10px] text-gray-500 mt-1 leading-tight">{nivel.descricao}</p>
                      </button>
                    ))}
                  </div>
                </div>
              </div>

              <div className="flex space-x-3 mt-6">
                <button onClick={() => setShowAddModal(false)}
                  className="flex-1 py-2.5 border border-gray-200 text-gray-600 font-bold text-sm rounded-lg hover:bg-gray-50 cursor-pointer whitespace-nowrap transition-colors">
                  Cancelar
                </button>
                <button onClick={handleAdicionarOperador}
                  className="flex-1 py-2.5 bg-gradient-to-r from-orange-500 to-amber-600 hover:from-orange-600 hover:to-amber-700 text-white font-bold text-sm rounded-lg transition-all cursor-pointer whitespace-nowrap shadow-md">
                  Adicionar
                </button>
              </div>
            </div>
          </div>
        )}

        {/* ─── MODAL: Editar Usuário ─────────────────── */}
        {showEditUserModal && (
          <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50" onClick={() => setShowEditUserModal(false)}>
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md mx-4 p-6" onClick={(e) => e.stopPropagation()}>
              <div className="flex items-center justify-between mb-5">
                <div>
                  <h3 className="text-lg font-bold text-gray-900">Editar Usuário</h3>
                  <p className="text-xs text-gray-500 mt-0.5">Atualize os dados do operador</p>
                </div>
                <button onClick={() => setShowEditUserModal(false)} className="p-2 hover:bg-gray-100 rounded-lg cursor-pointer">
                  <div className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-close-line text-gray-400"></i>
                  </div>
                </button>
              </div>

              {editUserError && (
                <div className="mb-4 p-3 bg-red-50 border border-red-100 rounded-lg flex items-center space-x-2">
                  <div className="w-4 h-4 flex items-center justify-center flex-shrink-0">
                    <i className="ri-error-warning-line text-red-500 text-sm"></i>
                  </div>
                  <p className="text-xs text-red-600 font-medium">{editUserError}</p>
                </div>
              )}

              <div className="space-y-4">
                <div>
                  <label className="block text-xs font-bold text-gray-700 mb-1.5">Nome completo</label>
                  <input type="text" value={editUserNome} onChange={(e) => setEditUserNome(e.target.value)} placeholder="Nome do operador"
                    className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm text-gray-800 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-orange-500/30 focus:border-orange-500 transition-all" />
                </div>
                <div>
                  <label className="block text-xs font-bold text-gray-700 mb-1.5">Email</label>
                  <input type="email" value={editUserEmail} onChange={(e) => setEditUserEmail(e.target.value)} placeholder="operador@empresa.com.br"
                    className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm text-gray-800 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-orange-500/30 focus:border-orange-500 transition-all" />
                </div>
                <div>
                  <label className="block text-xs font-bold text-gray-700 mb-1.5">CPF</label>
                  <input type="text" value={editUserCpf} onChange={(e) => setEditUserCpf(formatarCpf(e.target.value))} placeholder="000.000.000-00"
                    className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm text-gray-800 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-orange-500/30 focus:border-orange-500 transition-all" />
                </div>
                <div>
                  <label className="block text-xs font-bold text-gray-700 mb-1.5">Senha de acesso</label>
                  <input type="text" value={editUserSenha} onChange={(e) => setEditUserSenha(e.target.value)} placeholder="Mínimo 4 caracteres"
                    className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm text-gray-800 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-orange-500/30 focus:border-orange-500 transition-all" />
                </div>
              </div>

              <div className="flex space-x-3 mt-6">
                <button onClick={() => setShowEditUserModal(false)}
                  className="flex-1 py-2.5 border border-gray-200 text-gray-600 font-bold text-sm rounded-lg hover:bg-gray-50 cursor-pointer whitespace-nowrap transition-colors">
                  Cancelar
                </button>
                <button onClick={handleSalvarEdicaoUsuario}
                  className="flex-1 py-2.5 bg-gradient-to-r from-orange-500 to-amber-600 hover:from-orange-600 hover:to-amber-700 text-white font-bold text-sm rounded-lg transition-all cursor-pointer whitespace-nowrap shadow-md">
                  Salvar alterações
                </button>
              </div>
            </div>
          </div>
        )}

        {/* ─── MODAL: Criar/Editar Nível ────────────────── */}
        {showLevelModal && (
          <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50" onClick={() => { setShowLevelModal(false); setEditandoNivel(null); }}>
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl mx-4 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
              <div className="p-6">
                <div className="flex items-center justify-between mb-5">
                  <div>
                    <h3 className="text-lg font-bold text-gray-900">
                      {editandoNivel ? 'Editar Nível de Acesso' : 'Criar Nível de Acesso'}
                    </h3>
                    <p className="text-xs text-gray-500 mt-0.5">
                      {editandoNivel ? 'Ajuste as permissões deste nível' : 'Defina um novo perfil de acesso para os operadores'}
                    </p>
                  </div>
                  <button onClick={() => { setShowLevelModal(false); setEditandoNivel(null); }} className="p-2 hover:bg-gray-100 rounded-lg cursor-pointer">
                    <div className="w-4 h-4 flex items-center justify-center">
                      <i className="ri-close-line text-gray-400"></i>
                    </div>
                  </button>
                </div>

                {formNivelError && (
                  <div className="mb-4 p-3 bg-red-50 border border-red-100 rounded-lg flex items-center space-x-2">
                    <div className="w-4 h-4 flex items-center justify-center flex-shrink-0">
                      <i className="ri-error-warning-line text-red-500 text-sm"></i>
                    </div>
                    <p className="text-xs text-red-600 font-medium">{formNivelError}</p>
                  </div>
                )}

                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs font-bold text-gray-700 mb-1.5">Nome do nível</label>
                      <input type="text" value={formNivelNome} onChange={(e) => setFormNivelNome(e.target.value)} placeholder="Ex: Supervisor Regional"
                        className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm text-gray-800 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-orange-500/30 focus:border-orange-500 transition-all" />
                    </div>
                    <div>
                      <label className="block text-xs font-bold text-gray-700 mb-1.5">Cor do selo</label>
                      <div className="flex flex-wrap gap-1.5">
                        {CORES_DISPONIVEIS.map((cor) => (
                          <button key={cor.value} onClick={() => setFormNivelCor(cor.value)}
                            className={`w-8 h-8 rounded-full border-2 transition-all cursor-pointer ${cor.value.split(' ')[0]} ${formNivelCor === cor.value ? 'border-gray-800 ring-2 ring-gray-300 scale-110' : 'border-transparent hover:scale-105'}`}
                            title={cor.label} />
                        ))}
                      </div>
                    </div>
                  </div>

                  <div>
                    <label className="block text-xs font-bold text-gray-700 mb-1.5">Descrição</label>
                    <input type="text" value={formNivelDescricao} onChange={(e) => setFormNivelDescricao(e.target.value)} placeholder="Descreva o que este nível pode fazer"
                      className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm text-gray-800 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-orange-500/30 focus:border-orange-500 transition-all" />
                  </div>

                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <label className="text-xs font-bold text-gray-700">Permissões por módulo</label>
                      <div className="flex items-center space-x-3">
                        <button onClick={() => setFormNivelPermissoes(getTodosIdsModulos())} className="text-[10px] text-orange-600 hover:text-orange-700 font-bold cursor-pointer whitespace-nowrap">Marcar todos</button>
                        <button onClick={() => setFormNivelPermissoes([])} className="text-[10px] text-gray-500 hover:text-gray-600 font-bold cursor-pointer whitespace-nowrap">Limpar todos</button>
                      </div>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2 max-h-[300px] overflow-y-auto p-1">
                      {MODULOS_PERMISSOES.map((modulo) => {
                        if (modulo.submodulos) {
                          const subIds = modulo.submodulos.map((s) => s.id);
                          const todosSelecionados = subIds.every((id) => formNivelPermissoes.includes(id));
                          const parcial = subIds.some((id) => formNivelPermissoes.includes(id)) && !todosSelecionados;
                          return (
                            <div key={modulo.id} className="border border-gray-200 rounded-lg overflow-hidden">
                              <button onClick={() => toggleModuloComSubmodulos(modulo)}
                                className="w-full flex items-center justify-between px-3 py-2.5 bg-gray-50 hover:bg-gray-100 transition-colors cursor-pointer">
                                <div className="flex items-center space-x-2">
                                  <div className="w-4 h-4 flex items-center justify-center">
                                    <i className={`ri-${todosSelecionados ? 'checkbox-circle-fill text-emerald-500' : parcial ? 'checkbox-indeterminate-fill text-amber-500' : 'checkbox-blank-circle-line text-gray-300'} text-sm`}></i>
                                  </div>
                                  <span className="text-xs font-bold text-gray-700">{modulo.nome}</span>
                                </div>
                              </button>
                              <div className="px-3 py-2 space-y-1">
                                {modulo.submodulos.map((sub) => (
                                  <button key={sub.id} onClick={() => togglePermissao(sub.id)}
                                    className="w-full flex items-center space-x-2 py-1 hover:bg-gray-50 rounded cursor-pointer transition-colors">
                                    <div className="w-4 h-4 flex items-center justify-center flex-shrink-0">
                                      <i className={`ri-${formNivelPermissoes.includes(sub.id) ? 'checkbox-fill text-emerald-500' : 'checkbox-blank-line text-gray-300'} text-sm`}></i>
                                    </div>
                                    <span className="text-[11px] text-gray-600">{sub.nome}</span>
                                  </button>
                                ))}
                              </div>
                            </div>
                          );
                        }
                        return (
                          <button key={modulo.id} onClick={() => togglePermissao(modulo.id)}
                            className={`flex items-center space-x-2 p-3 rounded-lg border transition-all cursor-pointer ${formNivelPermissoes.includes(modulo.id) ? 'border-emerald-300 bg-emerald-50' : 'border-gray-200 hover:border-gray-300 bg-white'}`}>
                            <div className="w-4 h-4 flex items-center justify-center flex-shrink-0">
                              <i className={`ri-${formNivelPermissoes.includes(modulo.id) ? 'checkbox-fill text-emerald-500' : 'checkbox-blank-line text-gray-300'} text-sm`}></i>
                            </div>
                            <div className="w-4 h-4 flex items-center justify-center">
                              <i className={`${modulo.icone} ${formNivelPermissoes.includes(modulo.id) ? 'text-emerald-600' : 'text-gray-400'} text-sm`}></i>
                            </div>
                            <span className={`text-xs font-bold ${formNivelPermissoes.includes(modulo.id) ? 'text-emerald-700' : 'text-gray-600'}`}>{modulo.nome}</span>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                </div>

                <div className="flex space-x-3 mt-6">
                  <button onClick={() => { setShowLevelModal(false); setEditandoNivel(null); }}
                    className="flex-1 py-2.5 border border-gray-200 text-gray-600 font-bold text-sm rounded-lg hover:bg-gray-50 cursor-pointer whitespace-nowrap transition-colors">
                    Cancelar
                  </button>
                  <button onClick={handleSalvarNivel}
                    className="flex-1 py-2.5 bg-gradient-to-r from-orange-500 to-amber-600 hover:from-orange-600 hover:to-amber-700 text-white font-bold text-sm rounded-lg transition-all cursor-pointer whitespace-nowrap shadow-md">
                    {editandoNivel ? 'Salvar alterações' : 'Criar nível'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* ─── MODAL: Confirmar Exclusão ────────────────── */}
        {showDeleteConfirm && (
          <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50" onClick={() => setShowDeleteConfirm(null)}>
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm mx-4 p-6" onClick={(e) => e.stopPropagation()}>
              <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <div className="w-6 h-6 flex items-center justify-center">
                  <i className="ri-error-warning-line text-red-500 text-xl"></i>
                </div>
              </div>
              <h3 className="text-lg font-bold text-gray-900 text-center">Remover nível?</h3>
              <p className="text-sm text-gray-500 text-center mt-1">
                O nível <strong>{showDeleteConfirm.nome}</strong> será removido permanentemente.
                Operadores com este nível precisarão ser reatribuídos.
              </p>
              <div className="flex space-x-3 mt-5">
                <button onClick={() => setShowDeleteConfirm(null)}
                  className="flex-1 py-2.5 border border-gray-200 text-gray-600 font-bold text-sm rounded-lg hover:bg-gray-50 cursor-pointer whitespace-nowrap transition-colors">
                  Cancelar
                </button>
                <button onClick={handleExcluirNivel}
                  className="flex-1 py-2.5 bg-red-500 hover:bg-red-600 text-white font-bold text-sm rounded-lg cursor-pointer whitespace-nowrap transition-colors">
                  Remover
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Toast */}
        {toast && (
          <div className={`fixed bottom-6 right-6 px-4 py-3 rounded-xl shadow-lg text-sm font-bold flex items-center space-x-2 z-50 transition-all ${toast.type === 'success' ? 'bg-emerald-600 text-white' : 'bg-red-500 text-white'}`}>
            <div className="w-4 h-4 flex items-center justify-center">
              <i className={toast.type === 'success' ? 'ri-check-line text-sm' : 'ri-error-warning-line text-sm'}></i>
            </div>
            <span>{toast.message}</span>
          </div>
        )}
      </div>
    </Layout>
  );
}